import { useEffect, useState } from "react";
import type { ProjectBrief, ProjectResources } from "@/lib/api";

export interface Resource {
	id: string;
	name: string;
	selected: boolean;
	mimeType: string | null;
	/** Made by the creative agent: shown with the AI-generated label. */
	generated: boolean;
}

/**
 * Which of the conversation's files (and the brief) are attached to the next
 * chat message.
 */
export function useResourceSelection(
	resourceData: ProjectResources | undefined,
	briefData: ProjectBrief | undefined,
) {
	const [resources, setResources] = useState<Resource[]>([]);
	const [briefSelected, setBriefSelected] = useState(false);

	const files = resourceData?.files ?? [];

	// Sync files → resources.
	//
	// Only the conversation's files are selectable: selection feeds the chat,
	// which parses text out of them, and a creative is an image or a video with
	// no text to give.
	//
	// Keeps the user's picks across refetches (e.g. after an upload); new files
	// start unselected.
	useEffect(() => {
		setResources((prev) => {
			const picked = new Set(prev.filter((r) => r.selected).map((r) => r.id));
			return files.map((f) => ({
				id: f.id,
				name: f.title || "Untitled file",
				selected: picked.has(f.id),
				mimeType: f.mimeType,
				generated: f.source === "generated",
			}));
		});
	}, [resourceData]);

	// Derived, so it can't drift from the real selection.
	const hasSelectable = resources.length > 0 || !!briefData;
	const allSelected =
		hasSelectable &&
		resources.every((r) => r.selected) &&
		(!briefData || briefSelected);

	const toggleSelectAll = () => {
		const next = !allSelected;
		setResources((prev) => prev.map((r) => ({ ...r, selected: next })));
		if (briefData) setBriefSelected(next);
	};

	const toggleResource = (id: string) => {
		setResources((prev) =>
			prev.map((r) => (r.id === id ? { ...r, selected: !r.selected } : r)),
		);
	};

	const toggleBrief = () => {
		setBriefSelected((prev) => !prev);
	};

	const getSelectedFileIds = () => {
		const selectedFileIds = resources
			.filter((r) => r.selected)
			.map((r) => r.id);
		if (briefSelected && (briefData as any)?.id) {
			selectedFileIds.push((briefData as any).id as string);
		}
		return selectedFileIds;
	};

	return {
		resources,
		allSelected,
		briefSelected,
		toggleSelectAll,
		toggleResource,
		toggleBrief,
		getSelectedFileIds,
	};
}
